import { API_HEADERS } from "@sagot/constants";
import { useChatStream, workflowRequestFields, type ChatEvent, type ChatMessage } from "@sagot/chat";
import { useCallback, useState } from "react";
import { useClient } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";

/** A conversation the chat continues instead of starting a new one. */
export interface PlaygroundSeed {
  conversationId: string;
  messages: ChatMessage[];
}

/**
 * Playground chat over `POST /v1/agents/{id}/chat` (SSE). Auth is added per request (fresh
 * bearer + workspace header) while keeping the caller's abort signal, so Stop really closes
 * the stream and the server cancels generation. It declares `workflow_steps`, so a workflow that
 * answers a test message renders its real cards here and can be tried end to end. `seed` continues
 * a conversation that already exists (a workflow Preview starts one with the workflow's first lines).
 */
export function usePlaygroundChat(agentId: string, audience: number, seed?: PlaygroundSeed) {
  const client = useClient();
  const wsId = useWorkspaceId();
  const [messageId, setMessageId] = useState<string | null>(null);

  const fetchImpl = useCallback<typeof fetch>(
    async (input, init) => {
      const token = await client.session.ensureFresh();
      const headers = new Headers(init?.headers);
      if (token) headers.set(API_HEADERS.authorization, `Bearer ${token.value}`);
      headers.set(API_HEADERS.workspace, wsId);
      return globalThis.fetch(input, { ...init, headers, credentials: "include" });
    },
    [client.session, wsId],
  );

  const onEvent = useCallback((event: ChatEvent) => {
    if (event.type === "meta") setMessageId(event.data.message_id);
  }, []);

  const chat = useChatStream({
    endpoint: `${client.baseUrl}/v1/agents/${encodeURIComponent(agentId)}/chat`,
    fetchImpl,
    buildBody: ({ text, conversationId, clientMessageId, workflowReply }) => ({
      message: text,
      conversation_id: conversationId,
      client_message_id: clientMessageId,
      audience,
      ...workflowRequestFields(workflowReply),
    }),
    onEvent,
    conversationId: seed?.conversationId ?? null,
    initialMessages: seed?.messages,
  });

  const reset = useCallback(() => {
    chat.reset([], null);
    setMessageId(null);
  }, [chat]);

  return { ...chat, messageId, reset };
}
